import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BlueDotsBackground from '../../BlueDotsBackground';
import images from '../../content/mfpContent';

const lifecycle = [
  {
    iconClass: 'fas fa-folder-open',
    title: 'MTF Ingestion',
    description:
      'OpenPharma ingests complete claim-level detail from the MTF, delivering enhanced visibility and easy access for your teams.',
    image: images[0],
  },
  {
    iconClass: 'fas fa-calculator-simple',
    title: 'Accurate Rebates',
    description:
      'OpenPharma streamlines rebate accuracy by categorizing MFP claims according to clear rules. Smart claim categorization means the right rebate.',
    image: images[1],
  },
  {
    iconClass: 'fas fa-shield-check',
    title: 'Validations and Disputes',
    description:
      'With a comprehensive suite of MFP claim validations, OpenPharma ensures users can navigate the dispute process with accuracy and confidence.',
    image: images[2],
  },
  {
    iconClass: 'fas fa-handshake',
    title: 'Dispute Resolution',
    description:
      'OpenPharma facilitates open, good-faith resolution of disputed MFP claims through a secure portal connecting manufacturers and pharmacies.',
    image: images[3],
  },
  {
    iconClass: 'fas fa-money-bill-wave',
    title: 'Payments',
    description:
      'OpenPharma can connect with your current infrastructure to deliver clean, compliant payments that effectuate MFP claims.',
    image: images[4],
  },
  {
    iconClass: 'fas fa-paper-plane',
    title: 'Communication',
    description:
      'Once payments are confirmed, OpenPharma seamlessly communicates all required information to the MTF and downstream systems — including GP, Gross-to-Net, and more.',
    image: images[5],
  },
];

const benefits = [
  {
    iconClass: 'fas fa-search-dollar',
    title: '340B Designation',
    description:
      'OpenPharma delivers powerful validation tools to accurately identify Duplicate 340B MFP claims — giving your team the insight needed to take action and prevent costly revenue leakage.',
  },
  {
    iconClass: 'fas fa-link',
    title: 'Easy Integration',
    description:
      "Whether it's master data, payments, or 340B designation, OpenPharma integrates effortlessly into your existing ecosystem — enabling seamless, end-to-end processing of the MFP claim lifecycle.",
  },
];

const differentiators = [
  {
    iconClass: 'fas fa-sync-alt',
    title: 'Adaptibility',
    description:
      'With a rapidly evolving regulatory landscape, OpenPharma is built to seamlessly incorporate new requirements as they arise.',
  },
  {
    iconClass: 'fas fa-lightbulb',
    title: 'Strategic Insights',
    description:
      'OpenPharma turns your MFP data into actionable insights with intuitive dashboards and reporting tools. Gain real-time visibility, improve understanding across teams, and accelerate decision-making.',
  },
];

export default function MfpEffectuation() {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedIndex((prev) => (prev + 1) % lifecycle.length);
    }, 8000); // changes every 8 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <BlueDotsBackground />
      <section
        id="hero"
        className="relative text-center text-white overflow-hidden font-sans"
      >
        <div className="relative z-10 px-6 min-h-screen flex flex-col justify-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight leading-tight mb-8">
              Navigating IRA and MFP requirements?
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 font-light mb-12">
              OpenPharma is built to accommodate new contract and claim formats,
              including utilization-based claims from the MTF.
            </p>
            <p className="text-lg md:text-xl text-gray-300 font-light mb-12">
              It automates the end-to-end MFP workflow by managing master data,
              claims processing, CLD validation, 340B designation, dispute
              management through a centralized portal, and full credit ledger
              tracking, payment, and reconciliation to ensure compliance and
              operational efficiency.
            </p>
            <div className="max-w-xl w-full mx-auto mb-20"></div>
          </div>
        </div>
      </section>
      <section id="lifecycle" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Automated End-To-End Process
          </h2>
          <div className="h-[100px] flex items-center justify-center mb-10">
            <img
              src={lifecycle[highlightedIndex].image}
              alt="Automation Step Visual"
              className="h-full object-contain mx-auto rounded-lg shadow-lg"
            />
          </div>

          {/* Dynamic description under image */}
          <div className="h-[60px] mb-10 flex items-center justify-center transition-all duration-300">
            <p className="text-gray-300 text-base md:text-lg max-w-3xl mx-auto">
              {lifecycle[highlightedIndex].description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {lifecycle.map((step, idx) => (
              <div
                key={idx}
                className={`bg-gray-900 border rounded-xl p-6 shadow-md flex flex-col items-start gap-4 ${
                  highlightedIndex === idx ?
                    'border-blue-400 shadow-xl scale-105 transition-all'
                  : 'border-gray-700'
                }`}
              >
                <i className={`${step.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {step.title}
                </h3>
                {/* <p className="text-gray-300 text-sm">{automation.description}</p> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="benefits" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i className={`${benefit.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section
        id="differentiators"
        className="py-16 px-6 text-white text-center"
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            What Sets Us Apart
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {differentiators.map((differentiator, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i
                  className={`${differentiator.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {differentiator.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {differentiator.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="cta" className="py-16 px-6 text-white text-center">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center gap-6">
          <Link to="/contact">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Contact Us
            </button>
          </Link>
          <Link to="/login">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Explore Our Platform
            </button>
          </Link>
        </div>
      </section>
    </>
  );
}
